import {
  Component,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AfricaMapComponent } from '../../africa-map/africa-map.component';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { filter, take } from 'rxjs/operators';
import {
  IgxCategoryChartModule,
  IgxPieChartModule,
  IgxItemLegendModule,
  IgxLegendModule,
} from 'igniteui-angular-charts';
import { ReportsIntergrationService } from '../../services/reports-intergration.service';
import {
  CompletionTableData,
  MAndEData,
  MandEConfigModel,
  QuizPerformanceResponse,
} from '../../models/studentProfileModels';
import { KeycloakAuthService } from '../../services/keycloak-auth.service';
import { HttpHelperService } from '../../services/helper.service';
import { DateRangeDialogComponent } from '../../date-range-dialog/date-range-dialog.component';
import { ReportsBaseService } from '../../services/reports-base.service';
import { ChartJsComponent } from '../../shared/chart-js/chart-js.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TooltipService } from '../../services/tooltip.service';

export type KitsoaiLearningPathId = 'path1' | 'path2';

export interface KitsoaiLearningImpactView {
  usersBeganCourseTotal: number;
  usersBeganCourseChangeClass: string;
  usersBeganCourseChangeIcon: string;
  usersBeganCourseChangePercentage: string;
  courseCompletionsTotal: number;
  courseCompletionsChangeClass: string;
  courseCompletionsChangeIcon: string;
  courseCompletionsChangePercentage: string;
  completionRateTotal: number;
  completionRateChangeClass: string;
  completionRateChangeIcon: string;
  completionRateChangePercentage: string;
  prePostChart: any;
  prePostIndex: { quiz: string; color: string }[];
  prePostOptions: any;
  currentPeriodStats: QuizPerformanceResponse[];
  preQuizChart: any;
  postQuizChart: any;
  engagementChart: any;
  dropOffChart: any;
  financeTable: CompletionTableData[];
  mAndEResponses: MAndEData[];
  mAndEChart: any;
  mAndEConfig: MandEConfigModel[];
  avgImprovement: number;
  learningContentProgressTitle: string;
}

export interface KitsoaiFeedbackInsightPathway {
  label: string;
  pathway: KitsoaiLearningPathId;
}

/** English vs isiZulu counts for the User Language Selection card (Feedback & Insight). */
export interface KitsoaiUserLanguageSelectionBreakdown {
  englishCount: number;
  isiZuluCount: number;
  total: number;
  englishSharePct: number;
  isiZuluSharePct: number;
}

/** One row in the User Language Feedback card (dummy until API exists). */
export interface KitsoaiUserLanguageFeedbackRow {
  label: string;
  count: number;
  /** Share of total learners (0–100); drives bar width. */
  percentage: number;
  /** Second class on the bar fill (e.g. `--excellent`). */
  barFillClass: string;
}

@Component({
  selector: 'app-kitsoai-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatDialogModule,
    MatTableModule,
    IgxCategoryChartModule,
    IgxPieChartModule,
    IgxItemLegendModule,
    IgxLegendModule,
    MatTabsModule,
    MatCheckboxModule,
    MatTooltipModule,
    AfricaMapComponent,
    ChartJsComponent,
  ],
  templateUrl: './kitsoai-report.component.html',
  styleUrl: './kitsoai-report.component.scss',
})
export class KitsoaiReportComponent implements OnInit {
  trend: { percentage: number; color: string } | null = null;

  readonly kitsoaiFeedbackInsightPathways: KitsoaiFeedbackInsightPathway[] = [
    { label: 'English - A Pathway', pathway: 'path1' },
    { label: 'isiZulu Pathway', pathway: 'path2' },
  ];

  /**
   * Placeholder learner counts for User Language Selection (no backend yet).
   * Replace with API data when the endpoint exists.
   */
  private readonly kitsoaiUserLanguageSelectionDummyCounts = {
    englishLearners: 342,
    isiZuluLearners: 458,
  };

  /**
   * Placeholder for User Language Feedback (isiZulu Feedback & Insight). Replace when backend exists.
   */
  readonly kitsoaiUserLanguageFeedbackDummy: {
    totalLearners: number;
    rows: KitsoaiUserLanguageFeedbackRow[];
  } = {
    totalLearners: 800,
    rows: [
      {
        label: 'Excellent',
        count: 205,
        percentage: 25.6,
        barFillClass: 'kitsoai-language-selection-bar-fill--excellent',
      },
      {
        label: 'Good',
        count: 458,
        percentage: 57.25,
        barFillClass: 'kitsoai-language-selection-bar-fill--good',
      },
      {
        label: 'Could be better',
        count: 137,
        percentage: 17.13,
        barFillClass: 'kitsoai-language-selection-bar-fill--could-better',
      },
    ],
  };

  kitsoaiPath1PrePostQuizChartOptions: any = {
    indexAxis: 'y',
    plugins: {
      legend: { display: true, position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const val = context.parsed.x;
            return `${label}: ${val}%`;
          }
        }
      }
    },
    scales: {
      x: { beginAtZero: true, max: 100, ticks: { callback: (value: any) => value + '%' } },
      y: {
        ticks: {
          autoSkip: false,
          callback: (value: any, index: any) => {
            const config = this.baseService.prePostQuizQuestionsConfig || {};
            const customLabels = config['kitsoai_path1'] || [];
            const rawLabel = this.baseService.kitsoaiPath1PrePostChartData?.labels?.[index] as string || '';
            return customLabels[index] || rawLabel || value;
          }
        }
      }
    }
  };

  kitsoaiPath2PrePostQuizChartOptions: any = {
    indexAxis: 'y',
    plugins: {
      legend: { display: true, position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const val = context.parsed.x;
            return `${label}: ${val}%`;
          }
        }
      }
    },
    scales: {
      x: { beginAtZero: true, max: 100, ticks: { callback: (value: any) => value + '%' } },
      y: {
        ticks: {
          autoSkip: false,
          callback: (value: any, index: any) => {
            const config = this.baseService.prePostQuizQuestionsConfig || {};
            const customLabels = config['kitsoai_path2'] || [];
            const rawLabel = this.baseService.kitsoaiPath2PrePostChartData?.labels?.[index] as string || '';
            return customLabels[index] || rawLabel || value;
          }
        }
      }
    }
  };

  constructor(
    private reportsService: ReportsIntergrationService,
    private authService: KeycloakAuthService,
    private helperService: HttpHelperService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    public baseService: ReportsBaseService,
    public tooltips: TooltipService,
  ) { }

  ngOnInit(): void {
    this.baseService.updateDateRange();
    this.checkUserBotAccess();
  }

  openDateRangeDialog(): void {
    const dialogRef = this.dialog.open(DateRangeDialogComponent, {
      data: { startDate: this.baseService.startDate, endDate: this.baseService.endDate }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.baseService.startDate = result.startDate;
        this.baseService.endDate = result.endDate;
        this.onDateChange();
      }
    });
  }

  getPercentage(count: number): string {
    const total = this.baseService.countryData.reduce((sum, country) => sum + country.count, 0);
    return total === 0 ? '0' : ((count / total) * 100).toFixed(2);
  }

  private checkUserBotAccess(): void {
    this.baseService.isLoading = true;
    this.baseService.error = null;

    const user = this.authService.currentUserValue();
    if (user && user.roles) {
      this.processUserBotAccess(user);
      return;
    }

    this.authService.currentUser$
      .pipe(
        filter((u: any) => !!u && !!u.roles),
        take(1)
      )
      .subscribe({
        next: (u: any) => {
          this.processUserBotAccess(u);
        },
        error: (err) => {
          console.error('Error fetching user data:', err);
          this.baseService.error = "Failed to load user data. Please try logging in again.";
          this.baseService.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private processUserBotAccess(user: any): void {
    const botRoles = user.roles.filter((role: string) => role.startsWith('bot_access_'));
    this.baseService.availableBots = botRoles.map((role: string) => {
      const botName = role.replace('bot_access_', '');
      return {
        key: botName as 'kitsoai',
        name: botName.charAt(0).toUpperCase() + botName.slice(1)
      };
    });

    this.baseService.availableBots = this.baseService.availableBots.filter(bot => bot.key === 'kitsoai');

    if (this.baseService.availableBots.length > 0) {
      this.baseService.selectedBot = 'kitsoai';
      this.loadAllData();
    } else {
      this.baseService.error = "You do not have access to Kitsoai bot. Please contact an administrator.";
    }
    this.baseService.isLoading = false;
    this.cdr.detectChanges();
  }

  onDateChange(): void {
    this.baseService.clearData();
    this.baseService.updateDateRange();
    if (this.baseService.selectedBot) {
      this.loadAllData();
    }
  }

  onProvinceFilterChange(): void {
    this.baseService.updateProvinceChartForFilter();
    this.cdr.detectChanges();
  }

  onLanguageFilterChange(): void {
    this.baseService.updateLanguageChartForFilter();
    this.cdr.detectChanges();
  }

  resolveKitsoaiLearningImpactView(pathway: KitsoaiLearningPathId): KitsoaiLearningImpactView {
    const b = this.baseService;
    if (pathway === 'path1') {
      return {
        usersBeganCourseTotal: b.kitsoaiPath1UsersBeganCourseTotal,
        usersBeganCourseChangeClass: b.kitsoaiPath1UsersBeganCourseChangeClass,
        usersBeganCourseChangeIcon: b.kitsoaiPath1UsersBeganCourseChangeIcon,
        usersBeganCourseChangePercentage: b.kitsoaiPath1UsersBeganCourseChangePercentage,
        courseCompletionsTotal: b.kitsoaiPath1CourseCompletionsTotal,
        courseCompletionsChangeClass: b.kitsoaiPath1CourseCompletionsChangeClass,
        courseCompletionsChangeIcon: b.kitsoaiPath1CourseCompletionsChangeIcon,
        courseCompletionsChangePercentage: b.kitsoaiPath1CourseCompletionsChangePercentage,
        completionRateTotal: b.kitsoaiPath1CompletionRateTotal,
        completionRateChangeClass: b.kitsoaiPath1CompletionRateChangeClass,
        completionRateChangeIcon: b.kitsoaiPath1CompletionRateChangeIcon,
        completionRateChangePercentage: b.kitsoaiPath1CompletionRateChangePercentage,
        prePostChart: b.kitsoaiPath1PrePostChartData,
        prePostIndex: b.kitsoaiPath1PrePostQuizQuestionsIndexData,
        prePostOptions: this.kitsoaiPath1PrePostQuizChartOptions,
        currentPeriodStats: b.kitsoaiPath1CurrentPeriodStats,
        preQuizChart: b.kitsoaiPath1PreQuizScoresChartData,
        postQuizChart: b.kitsoaiPath1PostQuizScoresChartData,
        engagementChart: b.kitsoaiPath1OverAllEngagementChartData,
        dropOffChart: b.kitsoaiPath1DropOffChartData,
        financeTable: b.personalFinanceTableData,
        mAndEResponses: b.kitsoaiPath1MAndEResponses,
        mAndEChart: b.kitsoaiPath1MAndEChart,
        mAndEConfig: b.kitsoaiPath1MAndEConfigArray,
        avgImprovement: b.getAverageImprovement(b.kitsoaiPath1CurrentPeriodStats),
        learningContentProgressTitle: 'Learning Content Progress Overview AI & Me',
      };
    }
    return {
      usersBeganCourseTotal: b.kitsoaiPath2UsersBeganCourseTotal,
      usersBeganCourseChangeClass: b.kitsoaiPath2UsersBeganCourseChangeClass,
      usersBeganCourseChangeIcon: b.kitsoaiPath2UsersBeganCourseChangeIcon,
      usersBeganCourseChangePercentage: b.kitsoaiPath2UsersBeganCourseChangePercentage,
      courseCompletionsTotal: b.kitsoaiPath2CourseCompletionsTotal,
      courseCompletionsChangeClass: b.kitsoaiPath2CourseCompletionsChangeClass,
      courseCompletionsChangeIcon: b.kitsoaiPath2CourseCompletionsChangeIcon,
      courseCompletionsChangePercentage: b.kitsoaiPath2CourseCompletionsChangePercentage,
      completionRateTotal: b.kitsoaiPath2CompletionRateTotal,
      completionRateChangeClass: b.kitsoaiPath2CompletionRateChangeClass,
      completionRateChangeIcon: b.kitsoaiPath2CompletionRateChangeIcon,
      completionRateChangePercentage: b.kitsoaiPath2CompletionRateChangePercentage,
      prePostChart: b.kitsoaiPath2PrePostChartData,
      prePostIndex: b.kitsoaiPath2PrePostQuizQuestionsIndexData,
      prePostOptions: this.kitsoaiPath2PrePostQuizChartOptions,
      currentPeriodStats: b.kitsoaiPath2CurrentPeriodStats,
      preQuizChart: b.kitsoaiPath2PreQuizScoresChartData,
      postQuizChart: b.kitsoaiPath2PostQuizScoresChartData,
      engagementChart: b.kitsoaiPath2OverAllEngagementChartData,
      dropOffChart: b.kitsoaiPath2DropOffChartData,
      financeTable: b.businessFinanceTableData,
      mAndEResponses: b.kitsoaiPath2MAndEResponses,
      mAndEChart: b.kitsoaiPath2MAndEChart,
      mAndEConfig: b.kitsoaiPath2MAndEConfigArray,
      avgImprovement: b.getAverageImprovement(b.kitsoaiPath2CurrentPeriodStats),
        learningContentProgressTitle: 'Learning Content Progress Overview – IsiZulu',
    };
  }

  /**
   * User Language Selection card: dummy counts until backend is wired; bar widths use share of the two-way total.
   */
  get kitsoaiUserLanguageSelectionBreakdown(): KitsoaiUserLanguageSelectionBreakdown {
    const englishCount = this.kitsoaiUserLanguageSelectionDummyCounts.englishLearners;
    const isiZuluCount = this.kitsoaiUserLanguageSelectionDummyCounts.isiZuluLearners;
    const total = englishCount + isiZuluCount;
    const englishSharePct = total === 0 ? 0 : (englishCount / total) * 100;
    const isiZuluSharePct = total === 0 ? 0 : (isiZuluCount / total) * 100;
    return {
      englishCount,
      isiZuluCount,
      total,
      englishSharePct,
      isiZuluSharePct,
    };
  }

  private generateKitsoaiPrePostChartConfig(stats: QuizPerformanceResponse[] | null | undefined) {
    if (!stats || stats.length === 0) return null;
    const questions = stats.map(q => q.quizQuestion);
    const preData = stats.map(q => parseFloat(q.pre_learning_percentage) || 0);
    const postData = stats.map(q => parseFloat(q.post_learning_percentage) || 0);
    const preColor = 'rgba(235, 182, 6, 1)';
    const postColor = 'rgba(62, 132, 220, 1)';
    return {
      labels: questions,
      datasets: [
        {
          label: 'Pre-Learning',
          data: preData,
          backgroundColor: Array(preData.length).fill(preColor),
          borderColor: Array(preData.length).fill(preColor),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: this.baseService.prePostQuizBarThicknessPx,
        },
        {
          label: 'Post-Learning',
          data: postData,
          backgroundColor: Array(postData.length).fill(postColor),
          borderColor: Array(postData.length).fill(postColor),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: this.baseService.prePostQuizBarThicknessPx,
        }
      ]
    };
  }

  private buildPrePostQuizChartData() {
    const preColor = 'rgba(235, 182, 6, 1)';
    const postColor = 'rgba(62, 132, 220, 1)';
    const legend = [
      { quiz: 'Pre-Learning', color: preColor },
      { quiz: 'Post-Learning', color: postColor },
    ];

    this.baseService.kitsoaiPath1PrePostChartData = this.generateKitsoaiPrePostChartConfig(
      this.baseService.kitsoaiPath1CurrentPeriodStats
    );
    this.baseService.kitsoaiPath1PrePostQuizQuestionsIndexData = this.baseService.kitsoaiPath1PrePostChartData
      ? legend
      : [];

    this.baseService.kitsoaiPath2PrePostChartData = this.generateKitsoaiPrePostChartConfig(
      this.baseService.kitsoaiPath2CurrentPeriodStats
    );
    this.baseService.kitsoaiPath2PrePostQuizQuestionsIndexData = this.baseService.kitsoaiPath2PrePostChartData
      ? legend
      : [];
  }

  private createDoughnutConfig(value: number, colorKey: 'preQuiz' | 'postQuiz' | 'overAllEngagement', label: string) {
    const tone = this.baseService.pieChartColor[colorKey];
    const fill = Array.isArray(tone) ? tone[0] : tone;
    return {
      labels: [label, 'Remaining'],
      datasets: [{
        data: [value, 100 - value],
        backgroundColor: [fill, '#E0E0E0'],
        label
      }]
    };
  }

  private buildKitsoaiAssessmentCharts(): void {
    const b = this.baseService;

    const buildPathwayAssessmentCharts = (stats: QuizPerformanceResponse[], beganCourse: number) => {
      const pre = b.getAveragePreQuizScore(stats);
      const post = b.getAveragePostQuizScore(stats);
      const drop = b.calculateDropOffStats(stats, beganCourse);
      const engagement = b.getOverallEngagementStats(beganCourse, b.enrolledUsersTotal);
      return {
        preQuiz: this.createDoughnutConfig(pre, 'preQuiz', 'Average Pre-Quiz Score'),
        postQuiz: this.createDoughnutConfig(post, 'postQuiz', 'Average Post-Quiz Score'),
        engagement: this.createDoughnutConfig(engagement, 'overAllEngagement', 'Average Engagement Score'),
        dropOff: this.createDoughnutConfig(drop, 'overAllEngagement', 'Drop-Off Percentage'),
      };
    };

    const path1Charts = buildPathwayAssessmentCharts(
      b.kitsoaiPath1CurrentPeriodStats,
      b.kitsoaiPath1UsersBeganCourseTotal
    );
    b.kitsoaiPath1PreQuizScoresChartData = path1Charts.preQuiz;
    b.kitsoaiPath1PostQuizScoresChartData = path1Charts.postQuiz;
    b.kitsoaiPath1OverAllEngagementChartData = path1Charts.engagement;
    b.kitsoaiPath1DropOffChartData = path1Charts.dropOff;

    const path2Charts = buildPathwayAssessmentCharts(
      b.kitsoaiPath2CurrentPeriodStats,
      b.kitsoaiPath2UsersBeganCourseTotal
    );
    b.kitsoaiPath2PreQuizScoresChartData = path2Charts.preQuiz;
    b.kitsoaiPath2PostQuizScoresChartData = path2Charts.postQuiz;
    b.kitsoaiPath2OverAllEngagementChartData = path2Charts.engagement;
    b.kitsoaiPath2DropOffChartData = path2Charts.dropOff;
  }

  private buildKitsoaiMAndEChartData(pathway: KitsoaiLearningPathId): void {
    const rawData =
      pathway === 'path1'
        ? this.baseService.kitsoaiPath1MAndEAverages
        : this.baseService.kitsoaiPath2MAndEAverages;
    const chartConfig = this.baseService.buildFeedbackRatingOverTimeChartData(rawData);
    if (pathway === 'path1') {
      this.baseService.kitsoaiPath1MAndEChart = chartConfig;
    } else {
      this.baseService.kitsoaiPath2MAndEChart = chartConfig;
    }
  }

  private loadAllData(): Promise<void> {
    const localBot = this.baseService.selectedBot;
    if (!localBot) return Promise.resolve();

    this.baseService.isLoading = true;
    const currentStart = this.baseService.formatDate(this.baseService.startDate);
    const currentEnd = this.baseService.formatDate(this.baseService.endDate);
    const { prevStart, prevEnd } = this.baseService.getPreviousPeriod(this.baseService.startDate, this.baseService.endDate);

    const quizStats = this.baseService.loadPrePostQuizStats(localBot, currentStart, currentEnd);
    const engagement = this.baseService.loadEngagementCount(localBot, currentStart, currentEnd, prevStart, prevEnd);
    const completion = this.baseService.loadCompletionStats(localBot, currentStart, currentEnd, prevStart, prevEnd);
    const mAndEStats = this.baseService.loadMAndEStats(localBot, currentStart, currentEnd);
    const mAndENumber = this.baseService.loadMAndENumber(localBot, currentStart, currentEnd);

    return Promise.all([
      this.baseService.loadRemainingData(currentStart, currentEnd),
      this.baseService.loadBreakdowns(localBot, currentStart, currentEnd),
      this.baseService.loadTables(localBot, currentStart, currentEnd, prevStart, prevEnd),

      quizStats.then(() => {
        this.buildPrePostQuizChartData();
        this.cdr.detectChanges();
      }),

      Promise.all([quizStats, engagement, completion]).then(() => {
        this.buildKitsoaiAssessmentCharts();
        this.cdr.detectChanges();
      }),

      Promise.all([mAndEStats, mAndENumber]).then(() => {
        this.baseService.updateMAndEConfig();
        this.buildKitsoaiMAndEChartData('path1');
        this.buildKitsoaiMAndEChartData('path2');
        this.cdr.detectChanges();
      }),
    ])
      .then(() => {
        this.baseService.isLoading = false;
        this.cdr.markForCheck();
      })
      .catch((error) => {
        console.error('Error in loadAllData chain:', error);
        this.baseService.isLoading = false;
        this.baseService.error = 'Failed to load report data';
        this.cdr.detectChanges();
      });
  }
}
